import React, { useState } from 'react';
import styled from 'styled-components';
import { useCart } from '../contexts/CartContext';
import { useFavorites } from '../contexts/FavoritesContext';
import { useNavigate } from 'react-router-dom';
import { FaHeart } from 'react-icons/fa';
import ProductImageGallery from './ProductImageGallery';

const Card = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  transition: all 0.3s ease;
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: column;
  cursor: pointer;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  }
`;

const ImageContainer = styled.div`
  position: relative;
  height: 300px;
  overflow: hidden;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 60px;
    background: linear-gradient(to top, rgb(0 0 0 / 0.6), rgb(0 0 0 / 0));
    pointer-events: none;
    z-index: 1;
  }
  
  @media (max-width: 768px) {
    height: 180px;
    
    &::after {
      height: 40px;
    }
  }
`;

const Badge = styled.div`
  position: absolute;
  top: 10px;
  left: 10px;
  background: #ff4444;
  color: white;
  padding: 6px 10px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 700;
  z-index: 10;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
`;



const CardContent = styled.div`
  padding: 16px;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  
  @media (max-width: 768px) {
    padding: 10px;
  }
`;

const Brand = styled.div`
  color: #666;
  font-size: 12px;
  text-transform: uppercase;
  font-weight: 600;
  margin-bottom: 4px;
  
  @media (max-width: 768px) {
    font-size: 10px;
    margin-bottom: 2px;
  }
`;

const Title = styled.h3`
  color: #333;
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 8px 0;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  
  @media (max-width: 768px) {
    font-size: 13px;
    margin: 0 0 4px 0;
    -webkit-line-clamp: 2;
  }
`;

const Description = styled.p`
  color: #666;
  font-size: 14px;
  margin: 0 0 12px 0;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  flex-grow: 1;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const Rating = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  
  @media (max-width: 768px) {
    margin-bottom: 6px;
  }
`;

const Stars = styled.div`
  display: flex;
  margin-right: 8px;
`;

const Star = styled.span`
  color: ${props => props.$filled ? '#FFD700' : '#ddd'};
  font-size: 14px;
`;

const RatingText = styled.span`
  color: #666;
  font-size: 12px;
`;

const NutritionFacts = styled.div`
  margin-bottom: 12px;
  padding: 8px;
  background: #f8f9fa;
  border-radius: 6px;
  font-size: 11px;
  display: none;
`;

const NutritionTitle = styled.div`
  font-weight: 600;
  color: #333;
  margin-bottom: 4px;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const NutritionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 4px;
`;

const NutritionItem = styled.div`
  color: #666;
  display: flex;
  justify-content: space-between;
`;

const NutritionLabel = styled.span`
  color: #999;
`;

const NutritionValue = styled.span`
  color: #333;
  font-weight: 600;
`;

const PriceContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  
  @media (max-width: 768px) {
    margin-bottom: 8px;
  }
`;

const Price = styled.div`
  display: flex;
  flex-direction: column;
`;

const CurrentPrice = styled.span`
  color: #333;
  font-size: 18px;
  font-weight: 700;
  
  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const OldPrice = styled.span`
  color: #999;
  font-size: 14px;
  text-decoration: line-through;
`;

const Discount = styled.span`
  color: #ff4444;
  font-size: 12px;
  font-weight: 600;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 8px;
  margin-top: auto;
`;

const AddToCartButton = styled.button`
  flex: 1;
  background: #ff6b35;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 12px 16px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(255, 107, 53, 0.25);
  }

  @media (max-width: 768px) {
    padding: 8px 10px;
    font-size: 12px;
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const FavoriteButton = styled.button`
  background: ${props => props.$isFavorite ? '#ff6b35' : 'transparent'};
  color: ${props => props.$isFavorite ? '#fff' : '#ff6b35'};
  border: 1.5px solid #ff6b35;
  border-radius: 8px;
  padding: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover, &:focus {
    background: #ff6b35;
    color: #fff;
    border-color: #ff6b35;
  }

  svg {
    width: 16px;
    height: 16px;
    fill: currentColor;
    transition: fill 0.2s;
  }
`;

const LabelsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 10px;
`;

const LabelChip = styled.button`
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  background: #ff6b35;
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #e55a2b;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(255, 107, 53, 0.3);
  }
`;

const ProductCard = ({ product, onToggleFavorite, onQuickView }) => {
  const [isLoading, setIsLoading] = useState(false);
  const { addToCart, isInCart, getItemQuantity } = useCart();
  const { isFavorite: checkIsFavorite, addToFavorites, removeFromFavorites } = useFavorites();
  const navigate = useNavigate();
  
  const isFavorite = checkIsFavorite(product.id);

  const handleAddToCart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Проверяем наличие товара
    const stockQuantity = product.total_stock_quantity || 0;
    if (stockQuantity <= 0) {
      alert('Товар закончился, ожидайте');
      return;
    }
    
    setIsLoading(true);
    try {
      addToCart(product);
      // Небольшая задержка для визуального отклика
      setTimeout(() => {
        setIsLoading(false);
      }, 300);
    } catch (error) {
      console.error('Ошибка при добавлении в корзину:', error);
      setIsLoading(false);
    }
  };

  const isProductInCart = isInCart(product.id);
  const itemQuantity = getItemQuantity(product.id);



  const handleImageClick = () => {
    window.open(`/product/${product.id}`, '_blank', 'noopener,noreferrer');
  };

  const handleCardClick = (e) => {
    // Не открываем товар если кликнули по кнопке/ссылке внутри карточки
    if (e.target.closest('button') || e.target.closest('a')) return;
    window.open(`/product/${product.id}`, '_blank', 'noopener,noreferrer');
  };

  const renderStars = (rating) => {
    const ratingNum = typeof rating === 'number' ? rating : (parseFloat(rating) || 0);
    // Исправлено: используем index < ratingNum для правильного отображения дробных рейтингов
    return Array.from({ length: 5 }, (_, index) => (
      <Star key={index} $filled={index < ratingNum}>★</Star>
    ));
  };

  const discount = product.oldPrice ? 
    Math.round(((product.oldPrice - product.price) / product.oldPrice) * 100) : 0;

  // Парсинг данных КБЖУ
  const getNutritionFacts = () => {
    if (!product.nutrition_facts) return null;
    
    try {
      let nutrition = product.nutrition_facts;
      if (typeof nutrition === 'string') {
        nutrition = JSON.parse(nutrition);
      }
      
      // Проверяем, есть ли хотя бы одно заполненное поле
      if (nutrition.energy || nutrition.proteins || nutrition.fats || nutrition.carbs) {
        return nutrition;
      }
    } catch (e) {
      console.error('Ошибка парсинга nutrition_facts:', e);
    }
    return null;
  };

  const nutritionFacts = getNutritionFacts();

  return (
    <Card onClick={handleCardClick}>
      <ImageContainer>
        <ProductImageGallery 
          images={product.images || []}
          featuredImage={product.image || product.featured_image || '/nofoto.png'}
          alt={product.name}
          onImageClick={handleImageClick}
        />
        
        {discount > 0 && <Badge>-{discount}%</Badge>}
        
      </ImageContainer>

      <CardContent>
        {product.brand && <Brand>{product.brand}</Brand>}
        <Title>{product.name}</Title>
        
        {/* Метки товара */}
        {product.labels && Array.isArray(product.labels) && product.labels.length > 0 && (
          <LabelsContainer>
            {product.labels.slice(0, 3).map((label, index) => (
              <LabelChip
                key={index}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  navigate(`/catalog?label=${encodeURIComponent(label)}`);
                  // Перезагружаем страницу для применения фильтра
                  setTimeout(() => window.location.reload(), 100);
                }}
              >
                {label}
              </LabelChip>
            ))}
            {product.labels.length > 3 && (
              <LabelChip
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  navigate(`/catalog?label=${encodeURIComponent(product.labels[0])}`);
                  // Перезагружаем страницу для применения фильтра
                  setTimeout(() => window.location.reload(), 100);
                }}
              >
                +{product.labels.length - 3}
              </LabelChip>
            )}
          </LabelsContainer>
        )}
        
        {product.description && <Description>{product.description}</Description>}
        
        {nutritionFacts && (
          <NutritionFacts>
            <NutritionTitle>КБЖУ (100 г)</NutritionTitle>
            <NutritionGrid>
              {nutritionFacts.energy && (
                <NutritionItem>
                  <NutritionLabel>Энергия:</NutritionLabel>
                  <NutritionValue>{nutritionFacts.energy}</NutritionValue>
                </NutritionItem>
              )}
              {nutritionFacts.proteins && (
                <NutritionItem>
                  <NutritionLabel>Белки:</NutritionLabel>
                  <NutritionValue>{nutritionFacts.proteins}</NutritionValue>
                </NutritionItem>
              )}
              {nutritionFacts.fats && (
                <NutritionItem>
                  <NutritionLabel>Жиры:</NutritionLabel>
                  <NutritionValue>{nutritionFacts.fats}</NutritionValue>
                </NutritionItem>
              )}
              {nutritionFacts.carbs && (
                <NutritionItem>
                  <NutritionLabel>Углеводы:</NutritionLabel>
                  <NutritionValue>{nutritionFacts.carbs}</NutritionValue>
                </NutritionItem>
              )}
            </NutritionGrid>
          </NutritionFacts>
        )}
        
        <Rating>
          <Stars>{renderStars(product.rating || 0)}</Stars>
          <RatingText>({product.reviewsCount || 0})</RatingText>
        </Rating>

        <PriceContainer>
          <Price>
            <CurrentPrice>{product.price.toLocaleString()} ₽</CurrentPrice>
            {product.oldPrice && (
              <OldPrice>{product.oldPrice.toLocaleString()} ₽</OldPrice>
            )}
          </Price>
          {discount > 0 && <Discount>Скидка {discount}%</Discount>}
        </PriceContainer>

        <ButtonContainer onClick={(e) => e.stopPropagation()}>
          <AddToCartButton 
            type="button"
            onClick={handleAddToCart}
            disabled={isLoading || (product.total_stock_quantity || 0) <= 0}
          >
            {isLoading ? 'Добавляем...' : 
             (product.total_stock_quantity || 0) <= 0 ? 'Товар закончился' :
             isProductInCart ? `В корзине (${itemQuantity})` : 
             'В корзину'}
          </AddToCartButton>
          
          <FavoriteButton
            type="button"
            $isFavorite={isFavorite}
            onClick={async (e) => {
              e.preventDefault();
              e.stopPropagation();
              try {
                if (onToggleFavorite) {
                  onToggleFavorite(product.id, isFavorite);
                } else {
                  if (isFavorite) {
                    await removeFromFavorites(product.id);
                  } else {
                    await addToFavorites(product.id);
                  }
                }
              } catch (error) {
                console.error('Error toggling favorite:', error);
                if (error.message && error.message.includes('авторизация')) {
                  alert('Необходима авторизация для добавления в избранное');
                }
              }
            }}
            title={isFavorite ? 'Удалить из избранного' : 'Добавить в избранное'}
          >
            <FaHeart />
          </FavoriteButton>
        </ButtonContainer>
      </CardContent>
    </Card>
  );
};

export default ProductCard; 